import { Fragment, useState, useMemo, useCallback } from 'react';

import { Button } from '@mui/material';
import useApi from 'shared/hooks/api';
import api from 'utils/api';

import { PageLoader, PageError, Modal } from 'components';
import New from './newModal';
import { DOLPHIN_API } from 'shared/constants/apis';
import Details from '../components/DetailsModal';
import { tableColumns } from './tableColumns';
import BasicTable from 'components/BasicTable';
import { ConfirmModal } from 'shared/modals/confirmModal';

import logger from 'utils/logger';
import { useSnackbar } from 'notistack';

const log = logger.createLogger('Employments');

const IWGDashboardEmployments = () => {
  const [employmentSelected, setEmploymentSelected] = useState({
    organizationId: -1,
    userId: -1,
    userType: '',
    organizationName: '',
    userName: '',
  });
  const [employmentDataSelected, setEmploymentDataSelected] = useState({});
  const [employmentNewModalOpen, setEmploymentNewModalOpen] = useState(false);
  const [employmentDetailsModalOpen, setEmploymentDetailsModalOpen] =
    useState(false);
  const [employmentDeleteModalOpen, setEmploymentDeleteModalOpen] =
    useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const getChangableEmploymentData = (employment) => {
    return {
      isOwner: employment.isOwner,
      userType: employment.userType,
    };
  };

  const handleOpenDeleteEmployment = useCallback((employment) => {
    setEmploymentSelected({
      id: employment.id,
      organizationId: employment.organizationId,
      userId: employment.userId,
      userType: employment.userType,
      organizationName: employment.organizationName,
      userName: employment.userFullName,
    });
    setEmploymentDataSelected(() => ({
      ...getChangableEmploymentData(employment),
    }));
    setEmploymentDeleteModalOpen(true);
  }, []);

  const handleOpenDetailsEmployment = useCallback((employment) => {
    setEmploymentSelected({
      id: employment.id,
      organizationId: employment.organizationId,
      userId: employment.userId,
      organizationName: employment.organizationName,
      userType: employment.userType,
      userName: employment.userFullName,
    });
    setEmploymentDataSelected(() => ({
      ...getChangableEmploymentData(employment),
    }));
    setEmploymentDetailsModalOpen(true);
  }, []);

  const columns = useMemo(
    () => tableColumns(handleOpenDetailsEmployment, handleOpenDeleteEmployment),
    [handleOpenDetailsEmployment, handleOpenDeleteEmployment]
  );

  const [{ data, error }, fetchEmployments] = useApi.useGet(
    DOLPHIN_API.ADMIN_EMPLOYMENTS,
    {}
  );
  if (!data) return <PageLoader />;
  if (error)
    return (
      <PageError
        errorCode={error.response.status}
        customErrorMessage={error.response.data.message}
        customErrorDescription={error.response.data.description}
      />
    );

  const handleUpdateEmployment = async (id, employmentObject) => {
    const parsedData = JSON.parse(employmentObject);
    await api.put(DOLPHIN_API.ADMIN_EMPLOYMENTS + '/' + id, {
      data: parsedData,
    });

    await fetchEmployments();

    enqueueSnackbar('Employment updated successfully', {
      variant: 'success',
    });
  };

  const handleDeleteEmployment = async (employmentIds) => {
    try {
      await api.delete(
        DOLPHIN_API.ADMIN_EMPLOYMENTS + '/' + employmentIds.id,
        {}
      );
      await fetchEmployments();
      enqueueSnackbar('Employment deleted successfully', {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar('Error deleting employment: ' + error.message, {
        variant: 'error',
      });
    } finally {
      setEmploymentDeleteModalOpen(false);
    }
  };

  data.employments.sort((employment1, employment2) => {
    if (employment1.orgId > employment2.orgId) return 1;
    if (employment1.orgId < employment2.orgId) return -1;
    if (employment1.userId > employment2.userId) return 1;
    if (employment1.userId < employment2.userId) return -1;
    return 0;
  });

  const tableData = data.employments.map((employment) => {
    return {
      organizationName: employment.organizationName,
      userName: employment.userFullName,
      username: employment.userName,
      userType: employment.userType,
      isOwner: employment.isOwner ? 'True' : 'False',
      details: employment,
      delete: employment,
    };
  });

  return (
    <Fragment>
      <BasicTable
        columns={columns}
        data={tableData}
        tableTitle="Employments"
        handleRefreshClick={fetchEmployments}
        rowsPerPageOptions={[10, 20, 50, 100]}
        initialPageSize={50}
        customToolbar={
          <Button
            color="primary"
            variant="contained"
            onClick={() => setEmploymentNewModalOpen(true)}>
            New
          </Button>
        }
      />
      <Modal
        isOpen={employmentNewModalOpen}
        width="800px"
        withCloseIcon={true}
        onClose={() => setEmploymentNewModalOpen(false)}
        renderContent={(modal) => (
          <New close={modal.close} fetchEmployments={fetchEmployments} />
        )}
      />

      <Modal
        isOpen={employmentDetailsModalOpen}
        width="800px"
        withCloseIcon={true}
        onClose={() => setEmploymentDetailsModalOpen(false)}
        renderContent={() => (
          <Details
            dataType="Employment"
            title={`Employment: ${employmentSelected.organizationName}/${employmentSelected.userName}`}
            dataId={employmentSelected.id}
            data={employmentDataSelected}
            updateData={handleUpdateEmployment}
          />
        )}
      />

      <ConfirmModal
        isOpened={employmentDeleteModalOpen}
        onCancel={() => setEmploymentDeleteModalOpen(false)}
        onConfirm={() => handleDeleteEmployment(employmentSelected)}
        title={`Do you want to DELETE Employment: Organization: ${employmentSelected?.organizationName} - Employee: ${employmentSelected?.userName}`}
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </Fragment>
  );
};

export default IWGDashboardEmployments;
